import React, { useEffect, useState } from 'react';
import { Mutex, withTimeout } from 'async-mutex';
import { runFlinks } from '../../../services/integrationsService';
import { storeBankingEvents } from '../../../services/truststartService';
import { FlinksDetailsMap } from '../../../config/FlinksDetailsMap';
import { FlinksAPIDetailsMap } from '../../../config/FlinksAPIDetailsMap';

const sendEventMutex = withTimeout(new Mutex(), 4000);

const storeBankingEventsWithRaceCondition = async (events, requestId, clientId) => {
  try {
    if (!events.length) return;
    await storeBankingEvents({
      events: events.map((event) => ({
        smartConsentSessionId: event.smartConsentSessionId,
        timestamp: Date.now(),
        code: event.step ?? event.flinksCode,
        detail: FlinksDetailsMap[event.step] ?? FlinksAPIDetailsMap[event.flinksCode] ?? event.step,
        sessionDetails: event.loginId ? {
          loginId: event.loginId,
          flinksRequestId: event.requestId,
        } : null,
        isComplete: !!event?.loginId,
        provider: 'flinks',
        source: 'smartconsent',
        rawEvent: event,
      })),
      requestId,
      clientId,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Unable to store banking events', error);
  }
};

const safeStoreBankingEvents = async (setEvents, requestId, clientId) => {
  try {
    await sendEventMutex.runExclusive(async () => {
      // Atomic update of events
      const events = await new Promise((resolve) => {
        setEvents((currentEvents) => {
          resolve(currentEvents);
          return [];
        });
      });
      await storeBankingEventsWithRaceCondition(events, requestId, clientId);
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error storing banking events', error);
  }
};

function FlinksConnectIFrame(props) {
  const [, setEvents] = useState([]);

  const [smartConsentSessionId] = useState(Date.now());
  const {
    createScoringRequest,
    toggleSkipButton,
    onInstitutionSelected,
    onError,
    history,
    preventRedirectToSocial,
    onNextStep,
  } = props;

  useEffect(() => {
    async function onCrossFrameMessageReceived(event) {
      const { step, flinksCode } = event.data;
      if (!step && !flinksCode) return;

      setEvents((prevEvents) => [...prevEvents, { ...event.data, smartConsentSessionId }]);
      safeStoreBankingEvents(
        setEvents,
        props.requestId,
        props.clientId,
      );

      if (step === 'COMPONENT_LOAD_INSTITUTION_SELECTOR') {
        toggleSkipButton(true);
      } else if (step === 'INSTITUTION_SELECTED') {
        toggleSkipButton(false);
        onInstitutionSelected();
      } else if (step === 'REDIRECT') {
        const createScoringRequestFunction = createScoringRequest ?? (() => ({
          clientId: props.clientId,
          requestId: props.requestId,
        }));
        const createScoringRequestResult = await createScoringRequestFunction();
        const { clientId, requestId, error } = createScoringRequestResult;
        if (!requestId || error) {
          const message = 'Unable to redirect. Missing requestId or error is present.';
          // eslint-disable-next-line no-console
          console.error(message, { requestId, error });
          throw new Error(message);
        }

        const flinksResult = await runFlinks(
          requestId,
          clientId,
          {
            session: {
              loginId: event.data.loginId,
              institution: event.data.institution,
            },
          },
        );
        if (onError && (!flinksResult || flinksResult.errorMessage)) {
          onError();
        }
        if (preventRedirectToSocial) return;
        setTimeout(onNextStep(history, { scoringRequest: { } }), 5000);
      }
    }

    window.addEventListener('message', onCrossFrameMessageReceived);

    return () => {
      window.removeEventListener('message', onCrossFrameMessageReceived);
    };
  }, [
    smartConsentSessionId,
    createScoringRequest,
    toggleSkipButton,
    onInstitutionSelected,
    onError,
    history,
    preventRedirectToSocial,
    onNextStep,
    props.clientId,
    props.requestId,
  ]);

  function onIframeLoaded() {
    // this function will be called every time the iframe load a new page for example thank-you.html
    if (!props.loading) return;

    props.hideLoader();
  }

  return (
    <iframe
      onLoad={onIframeLoaded}
      title="Flinks Connect"
      className="flinksconnect"
      height="760"
      src={props.url}
      data-cy="flinksconnect"
    />
  );
}

export default FlinksConnectIFrame;
